import {
	parseSensorRanging,
	SensorCommandCode,
	type SensorEchoClassification,
	type SensorSettingsModel,
} from '@supply-mover-2-0/core';
import type {
	DiscoveredSensor,
	SensorScanState,
	SensorUnitReadModel,
} from './SensorConfigService';
import { withBleTimeout } from './SensorConfigService';

/**
 * PD-15775/76/78/79 — a scriptable in-memory sensor fleet for testing the
 * provisioning flow OFF-SITE: the real flow needs physical Meskernel units
 * in Bluetooth range, so without this every change to the screen could only
 * be verified during an install visit (owner direction 2026-08-30: mock the
 * Bluetooth integration).
 *
 * DEV/TEST ONLY. Activated by a localStorage flag — never in normal use:
 *
 *   localStorage.setItem('workd:sensor-ble-mock', JSON.stringify([...units]))
 *   (or set it to '1' for the default fleet below, then reload)
 *
 * The mock implements the same public surface as SensorConfigServiceImpl;
 * the exported SensorConfigService delegates to it when the flag is set.
 * Behaviors that matter to the screen are scriptable per unit:
 *   - `rejectCodes`: command codes the unit REJECTS (ack result 'fail=-3').
 *   - `ignoreWriteCodes`: codes the unit ACKS but silently does not store —
 *     the field failure mode behind PD-15779's read-back verification.
 *   - `hangOnAction`: sendAction never resolves — exercises the PD-15776
 *     operation timeout. Pair with a short timeout override in tests.
 */

export interface MockSensorUnitConfig {
	name: string;
	serial: string;
	code: string;
	rssi?: number;
	moduleCount?: number | null;
	/** Addressed module ids (the `onine_dev` map); [] = none addressed. */
	addressedModules?: Array<number>;
	serverUrl?: string | null;
	apiKey?: string | null;
	reportInterval?: number | null;
	addrEncodeMode?: number;
	firmwareVersion?: string;
	rejectCodes?: Array<string>;
	ignoreWriteCodes?: Array<string>;
	hangOnAction?: boolean;
}

const DEFAULT_FLEET: Array<MockSensorUnitConfig> = [
	{
		// Deliberately out of alphabetical order to prove the sorted list.
		name: 'MotorCityICE_9DFF85CD',
		serial: 'MOCK26AA0000001P',
		code: '9DFF85CD',
		rssi: -67,
		moduleCount: 4,
		addressedModules: [],
		reportInterval: 7200,
		addrEncodeMode: 1,
		// The field failure: the unit acks the interval write but the value
		// never sticks (PD-15779).
		ignoreWriteCodes: [SensorCommandCode.ReportInterval],
	},
	{
		name: 'MotorCityICE_34D4E7EC',
		serial: 'MOCK26AA0000002P',
		code: '34D4E7EC',
		rssi: -69,
		moduleCount: 4,
		addressedModules: [],
		reportInterval: 7200,
		addrEncodeMode: 1,
	},
	{
		name: 'MotorCityICE_17D3E7EC',
		serial: 'MOCK26AA0000003P',
		code: '17D3E7EC',
		rssi: -92,
		moduleCount: 3,
		addressedModules: [1, 2, 3],
		reportInterval: 600,
		addrEncodeMode: 0,
		// Hangs on re-commission actions — exercises the operation timeout
		// (PD-15776): the buttons must come back with a clear error.
		hangOnAction: true,
	},
];

const STORAGE_KEY = 'workd:sensor-ble-mock';

export function readMockFleetConfig(): Array<MockSensorUnitConfig> | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw == null || raw === '') {
			return null;
		}
		if (raw === '1' || raw === 'true') {
			return DEFAULT_FLEET;
		}
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed)
			? (parsed as Array<MockSensorUnitConfig>)
			: DEFAULT_FLEET;
	} catch {
		return null;
	}
}

interface MockUnitState {
	config: MockSensorUnitConfig;
	settings: {
		serverUrl: string | null;
		apiKey: string | null;
		reportInterval: number | null;
		moduleCount: number | null;
		addrEncodeMode: number;
		addressedModules: Array<number>;
	};
}

function toSettingsModel(state: MockUnitState): SensorSettingsModel {
	const slots = state.settings.moduleCount ?? 4;
	return {
		serverUrl: state.settings.serverUrl,
		apiKey: state.settings.apiKey,
		apnName: 'hologram',
		moduleCount: state.settings.moduleCount,
		reportInterval: state.settings.reportInterval,
		addrEncodeMode: state.settings.addrEncodeMode,
		moduleAddresses: Array.from({ length: slots }, (_, index) => ({
			index,
			address: state.settings.addressedModules.includes(index + 1)
				? String(index + 1)
				: 'FF',
		})),
		raw: '(mock)',
	};
}

/** Same public surface the screen uses on SensorConfigServiceImpl. */
export class MockSensorConfigService {
	private readonly _units = new Map<string, MockUnitState>();
	private _connectedDeviceId: string | null = null;
	private _scanning = false;
	private readonly _listeners = new Set<(state: SensorScanState) => void>();

	public constructor(fleet: Array<MockSensorUnitConfig>) {
		for (const config of fleet) {
			this._units.set(`mock-${config.code}`, {
				config,
				settings: {
					serverUrl: config.serverUrl ?? null,
					apiKey: config.apiKey ?? null,
					reportInterval: config.reportInterval ?? null,
					moduleCount: config.moduleCount ?? null,
					addrEncodeMode: config.addrEncodeMode ?? 0,
					addressedModules: [...(config.addressedModules ?? [])],
				},
			});
		}
	}

	public get state(): SensorScanState {
		return {
			scanning: this._scanning,
			connectedDeviceId: this._connectedDeviceId,
		};
	}

	public subscribe(listener: (state: SensorScanState) => void): () => void {
		this._listeners.add(listener);
		listener(this.state);
		return () => this._listeners.delete(listener);
	}

	public supportsFleetScan(): boolean {
		return true;
	}

	public isBluetoothSupported(): boolean {
		return true;
	}

	public async scan(
		_prefixes: ReadonlyArray<string>,
		onDevice: (device: DiscoveredSensor) => void,
	): Promise<void> {
		this._scanning = true;
		// Surface units in config order (deliberately unsorted) with a small
		// stagger, like a real advertising sweep.
		for (const [deviceId, unit] of this._units) {
			await new Promise((resolve) => setTimeout(resolve, 120));
			onDevice({
				deviceId,
				name: unit.config.name,
				code: unit.config.code,
				rssi: unit.config.rssi ?? null,
			});
		}
		this._scanning = false;
	}

	public rediscoverUnit(
		match: { name: string; code: string | null },
		_prefixes: ReadonlyArray<string>,
	): Promise<DiscoveredSensor | null> {
		for (const [deviceId, unit] of this._units) {
			if (unit.config.name === match.name) {
				return Promise.resolve({
					deviceId,
					name: unit.config.name,
					code: unit.config.code,
					rssi: unit.config.rssi ?? null,
				});
			}
		}
		return Promise.resolve(null);
	}

	public stopScan(): Promise<void> {
		this._scanning = false;
		return Promise.resolve();
	}

	public pickUnit(
		_prefixes: ReadonlyArray<string>,
	): Promise<DiscoveredSensor | null> {
		const first = [...this._units.entries()][0];
		if (first == null) {
			return Promise.resolve(null);
		}
		return Promise.resolve({
			deviceId: first[0],
			name: first[1].config.name,
			code: first[1].config.code,
			rssi: first[1].config.rssi ?? null,
		});
	}

	public connect(deviceId: string): Promise<void> {
		this._connectedDeviceId = deviceId;
		return Promise.resolve();
	}

	public disconnect(): Promise<void> {
		this._connectedDeviceId = null;
		return Promise.resolve();
	}

	public async waitForUnit(): Promise<void> {
		// Mock units are always reachable.
	}

	public readUnit(
		deviceId: string,
		_options?: { keepConnection?: boolean },
	): Promise<SensorUnitReadModel> {
		const unit = this._requireUnit(deviceId);
		return Promise.resolve({
			deviceId,
			identity: {
				manufacturer: 'Meskernel (mock)',
				deviceName: 'LDM',
				serialNumber: unit.config.serial,
				hardwareVersion: '631K_MS',
				firmwareVersion: unit.config.firmwareVersion ?? '26061500',
				raw: '(mock)',
			},
			settings: toSettingsModel(unit),
			status: {
				powerSource: 1,
				charging: 1,
				batteryMillivolts: 4120,
				temperatureC: 21,
				networkType: 'LTE',
				registered: 1,
				signalQuality: 24,
				signalStrength: -64,
				bitErrorRate: null,
				referenceSignalQuality: null,
				referenceSignalPower: null,
				raw: 'power=AC,chg=1,vbat=4120 (mock)',
			},
			// PD-16068 — sample ranging read shaped like a real freezer unit:
			// wrapped below-freezing temperatures and one module out of range.
			ranging: parseSensorRanging(
				'd=312,845,2147483647,1190,s=231,240,0,226,t=4294967272,4294967253,4294967294,4294967115',
			),
			readAt: new Date(),
		});
	}

	public readSettings(deviceId: string): Promise<SensorSettingsModel> {
		return Promise.resolve(toSettingsModel(this._requireUnit(deviceId)));
	}

	public sendCommand(
		deviceId: string,
		_commandDeviceId: string,
		code: SensorCommandCode,
		value: string | number,
	): Promise<SensorEchoClassification> {
		const unit = this._requireUnit(deviceId);
		if (unit.config.rejectCodes?.includes(code) === true) {
			return Promise.resolve({
				parsed: true,
				result: 'fail=-3',
				rawReadback: false,
				rawEcho: `:${unit.config.code}:${code}:fail=-3`,
			});
		}
		const stored = unit.config.ignoreWriteCodes?.includes(code) !== true;
		if (stored) {
			const text = String(value);
			if (code === SensorCommandCode.ServerUrl) {
				unit.settings.serverUrl = text;
			} else if (code === SensorCommandCode.ApiKey) {
				unit.settings.apiKey = text;
			} else if (code === SensorCommandCode.ReportInterval) {
				unit.settings.reportInterval = Number.parseInt(text, 10);
			} else if (code === SensorCommandCode.ModuleCount) {
				unit.settings.moduleCount = Number.parseInt(text, 10);
			}
		}
		return Promise.resolve({
			parsed: true,
			result: 'success',
			rawReadback: false,
			rawEcho: `:${unit.config.code}:${code}:success`,
		});
	}

	public sendActionFireAndForget(): Promise<void> {
		// Reboots are fire-and-forget on the real service too.
		return Promise.resolve();
	}

	public async sendAction(
		deviceId: string,
		_commandDeviceId: string,
		code: SensorCommandCode,
	): Promise<SensorEchoClassification> {
		const unit = this._requireUnit(deviceId);
		if (unit.config.hangOnAction === true) {
			// A write that never settles, behind a short ceiling so the UI's
			// recovery path (buttons re-enable, clear error) is exercised the
			// same way the production timeout produces it.
			return withBleTimeout(
				new Promise<SensorEchoClassification>(() => {
					// Intentionally unresolved.
				}),
				'write-failed',
				'The command',
				8_000,
			);
		}
		if (code === SensorCommandCode.ClearAddressing) {
			unit.settings.addressedModules = [];
			unit.settings.addrEncodeMode = 1;
		} else if (code === SensorCommandCode.EnableAddressing) {
			unit.settings.addrEncodeMode = 1;
		}
		return {
			parsed: true,
			result: 'success',
			rawReadback: false,
			rawEcho: `:${unit.config.code}:${code}:success`,
		};
	}

	private _requireUnit(deviceId: string): MockUnitState {
		const unit = this._units.get(deviceId);
		if (unit == null) {
			throw new Error(`Mock sensor unit ${deviceId} not found`);
		}
		return unit;
	}
}
